import React from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import MdSystemUpdateAlt from '@meronex/icons/md/MdSystemUpdateAlt';
import LoadingBtn from '../buttons/LoadingBtn';
import { AppContext } from '../../base/AppContext';
import Fade from '@material-ui/core/Fade';
import Confetti from 'react-confetti';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CLIENT_STAMP = process.env.REACT_APP_BUILD_STAMP;

const toComparableTime = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const toComparableNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const pickLatestMetadataDoc = (docs = []) => {
  if (!Array.isArray(docs) || docs.length === 0) return null;

  return [...docs].sort((a, b) => {
    const createdAtDiff =
      toComparableTime(b?.createdAt) - toComparableTime(a?.createdAt);
    if (createdAtDiff !== 0) return createdAtDiff;

    const buildStampDiff =
      toComparableNumber(b?.buildStamp) - toComparableNumber(a?.buildStamp);
    if (buildStampDiff !== 0) return buildStampDiff;

    return toComparableNumber(b?.build) - toComparableNumber(a?.build);
  })[0];
};

function VersionChecker() {
  const { App } = React.useContext(AppContext);
  const { server } = App;
  const [showDialog, setShowDialog] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const dismissedRef = React.useRef(false);

  const isStale = React.useCallback((serverStamp) => {
    return (
      CLIENT_STAMP &&
      serverStamp &&
      String(serverStamp) !== String(CLIENT_STAMP)
    );
  }, []);

  // Fetch public/metadata.json from CDN — fallback for when
  // build-update.js didn't push to Meteor
  const checkViaFetch = React.useCallback(async () => {
    if (!CLIENT_STAMP) return;
    try {
      const metadataUrl = new URL(
        `${process.env.PUBLIC_URL}/metadata.json`,
        window.location.href
      );
      metadataUrl.searchParams.set('_v', Date.now());
      const response = await window.fetch(
        metadataUrl.toString(),
        { cache: 'no-store' }
      );
      if (!response.ok) return;
      const fresh = await response.json();
      console.log(
        `[App/versionChecker] Fetch check — client: ${CLIENT_STAMP}, server: ${fresh.buildStamp}`
      );
      if (!dismissedRef.current) setShowDialog(isStale(fresh.buildStamp));
    } catch (e) {
      console.warn('[App/versionChecker] Fetch failed:', e);
    }
  }, [isStale]);

  React.useEffect(() => {
    if (App.isLocalHost()) return;

    // Primary: DDP subscription — instant push from Meteor
    if (server) {
      App.eventsManager.on('event-connected', VersionChecker.name, () => {
        const subscribe = async () => {
          try {
            const sub = server.subscribe('metadata', {
              sort: { createdAt: -1 },
              limit: 1,
            });
            if (sub) await sub.ready();
          } catch (e) {}

          const reactive = server.collection('metadata').reactive();
          const check = () => {
            const doc = pickLatestMetadataDoc(reactive.data());
            if (doc) {
              console.log(
                `[App/versionChecker] DDP check — client: ${CLIENT_STAMP}, server: ${doc.buildStamp}`
              );
              if (!dismissedRef.current) setShowDialog(isStale(doc.buildStamp));
            } else {
              setShowDialog(false);
            }
          };
          reactive.onChange(check);
          check();
        };
        subscribe();
      });
    }

    // Fallback: fetch on focus (covers deploys without build-update.js)
    const onFocus = () => checkViaFetch();
    window.addEventListener('focus', onFocus);

    // Also check on first load
    checkViaFetch();

    return () => {
      window.removeEventListener('focus', onFocus);
      if (server) {
        App.eventsManager.removeListener('event-connected', VersionChecker.name);
      }
    };
  }, [App, checkViaFetch, server]);

  return (
    <div>
      <Dialog
        className={'version-checker-dialog'}
        open={showDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => { dismissedRef.current = true; setShowDialog(false); }}>
        <DialogTitle id="alert-dialog-slide-title">
          <span>
            <span role="img" aria-label="party popper">
              🎉
            </span>
            &nbsp;&nbsp;&nbsp;New Update Available
          </span>
        </DialogTitle>
        <DialogContent>
          <div id="alert-dialog-slide-description">
            A new version of the app is available. Click below to update.
          </div>
          <div
            style={{
              display: updating ? 'inline' : 'none',
            }}>
            <Fade in={updating}>
              <Confetti
                numberOfPieces={50}
                gravity={0.1}
                tweenDuration={1200}
              />
            </Fade>
          </div>
          <LoadingBtn
            style={{
              width: '100%',
              borderRadius: '20px',
              marginTop: '20px',
              marginBottom: '20px',
            }}
            loading={updating}
            onClick={() => {
              setShowDialog(false);
              setUpdating(true);
              setTimeout(() => {
                App.forceReload();
              }, 500);
            }}>
            <span style={{ marginRight: '5px' }}>Update</span>
            <MdSystemUpdateAlt />
          </LoadingBtn>
        </DialogContent>
      </Dialog>
    </div>
  );
}

VersionChecker.propTypes = {};
VersionChecker.defaultProps = {};
export default VersionChecker;
